import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import OfferCard from "@/components/OfferCard";
import EmptyState from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Filter, SearchX } from "lucide-react";

export default function Offers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [onlyMatched, setOnlyMatched] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (onlyMatched !== "all") params.matched = onlyMatched === "yes";
    const { data } = await api.get("/offers", { params });
    setItems(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, onlyMatched]);

  const changeStatus = async (offer, newStatus) => {
    try {
      await api.patch(`/offers/${offer.id}/status`, { status: newStatus });
      setItems((prev) => prev.map((o) => (o.id === offer.id ? { ...o, status: newStatus } : o)));
      toast.success("Status zaktualizowany");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = items.filter((o) => {
    if (!q) return true;
    const t = `${o.title} ${o.school} ${o.city} ${(o.subjects || []).join(" ")}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-bold mb-2">Baza ofert</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-50">Wszystkie oferty</h1>
        <p className="text-slate-400 mt-2 text-sm">Kompletna lista z możliwością filtrowania i oznaczania statusu.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
        <Filter className="w-4 h-4 text-slate-500" />
        <Input
          data-testid="offers-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj po tytule, szkole, mieście..."
          className="max-w-xs h-9 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px] h-9 bg-slate-950/60 border-slate-800" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie statusy</SelectItem>
            <SelectItem value="new">Nowe</SelectItem>
            <SelectItem value="applied">Aplikowałem</SelectItem>
            <SelectItem value="rejected">Odrzucone</SelectItem>
          </SelectContent>
        </Select>
        <Select value={onlyMatched} onValueChange={setOnlyMatched}>
          <SelectTrigger className="w-[200px] h-9 bg-slate-950/60 border-slate-800" data-testid="filter-matched">
            <SelectValue placeholder="Dopasowanie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie</SelectItem>
            <SelectItem value="yes">Tylko dopasowane</SelectItem>
            <SelectItem value="no">Bez dopasowania</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-slate-500" data-testid="offers-count">
          {filtered.length} / {items.length}
        </span>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[200px] bg-slate-900/60" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          testid="offers-empty"
          title="Nic tu jeszcze nie ma"
          description="Uruchom skanowanie z dashboardu, aby pobrać oferty z kuratorium."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((o) => (
            <OfferCard key={o.id} offer={o} onStatusChange={changeStatus} />
          ))}
        </div>
      )}
    </div>
  );
}
